import { useAuthStore } from "@lib/auth";
import type { ReactNode } from "react";
import { useOrgMembers } from "@/api/org-member";
import { useTaskTags } from "@/api/task";
import { TableFilter } from "@/components/ui/combobox";
import { FiltersContainer, ResetFilters, SearchFilter } from "@/components/ui/filters";
import { useArraySearchParamFilter } from "@/hooks/use-search-param-filter";
import { getUserName, TASK_STATUS_LABELS } from "./shared";

export const TASK_FILTER_KEYS = ["query", "status", "user", "due", "tags"];

const STATUS_OPTIONS = Object.entries(TASK_STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const DUE_OPTIONS = [
  { value: "overdue", label: "Overdue" },
  { value: "today", label: "Due today" },
  { value: "week", label: "Due this week" },
];

function StatusFilter() {
  const [value, setValue] = useArraySearchParamFilter("status");
  return (
    <TableFilter
      type="checkbox"
      title="Status"
      value={value}
      options={STATUS_OPTIONS}
      onChange={setValue}
      searchable={false}
    />
  );
}

function AssigneeFilter() {
  const org = useAuthStore((state) => state.org);
  const [value, setValue] = useArraySearchParamFilter("user");
  const { data: members } = useOrgMembers(org?.id ?? "", undefined, { skip: !org?.id });

  const options = [
    { value: "me", label: "Assigned to me" },
    { value: "null", label: "Unassigned" },
    ...(members ?? []).map((member) => ({
      value: member.user.id,
      label: getUserName(member.user),
    })),
  ];

  return (
    <TableFilter
      type="radio"
      title="Assignee"
      value={value}
      options={options}
      onChange={setValue}
    />
  );
}

function DueFilter() {
  const [value, setValue] = useArraySearchParamFilter("due");
  return (
    <TableFilter
      type="radio"
      title="Due"
      value={value}
      options={DUE_OPTIONS}
      onChange={setValue}
      searchable={false}
    />
  );
}

function TagsFilter({ gym }: { gym?: string }) {
  const [value, setValue] = useArraySearchParamFilter("tags");
  const { data } = useTaskTags(gym);
  const options = (data?.tags ?? []).map((tag) => ({ value: tag, label: tag }));
  if (options.length === 0 && value.length === 0) return null;
  return (
    <TableFilter type="checkbox" title="Tags" value={value} options={options} onChange={setValue} />
  );
}

export function TaskFilters({
  gym,
  hideStatus,
  trailing,
  className,
}: {
  gym?: string;
  /** Board view has fixed status columns, so the status filter is noise. */
  hideStatus?: boolean;
  /** Right-aligned slot (view toggle). */
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <FiltersContainer className={className}>
      <SearchFilter placeholder="Search tasks..." />
      {!hideStatus && <StatusFilter />}
      <AssigneeFilter />
      <DueFilter />
      <TagsFilter gym={gym} />
      <div className="ml-auto flex items-center gap-2 pl-2">
        <ResetFilters title="Clear all" filterKeys={TASK_FILTER_KEYS} />
        {trailing}
      </div>
    </FiltersContainer>
  );
}
